import { Taxonomy } from "@common/categories";
import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { NamedView, ViewType } from "@common/types/views/views";
import { useMemo } from "react";
import { useClasses } from "@api/class.api";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useStandardCategories } from "@hooks/useStandardCategories";
import { useIsRecognitionScheduleColumnEnabled } from "@hooks/useIsRecognitionScheduleColumnEnabled";
import { createDefaultTransactionView } from "./DefaultTransactionView";
import { createSchedulesReviewView } from "./SchedulesReviewView";
import {
    createGlFirstTransferReviewView,
    createTransferReviewView,
} from "./TransferReviewView";
import { createUnreconciledChecksView } from "./UnreconciledChecksView";

const createLedgerReviewView = (
    entityIds: number[],
): NamedView<TransactionView> => ({
    id: "ledger-review",
    name: "Cash ledger review",
    type: ViewType.TRANSACTION,
    options: {
        bulkActions: true,
        selection: true,
    },
    columns: [
        {
            id: TransactionColumn.LOCK,
            options: {
                pin: "left",
            },
        },
        {
            id: TransactionColumn.DATE,
            options: {
                pin: "left",
            },
        },
        {
            id: TransactionColumn.SPLIT,
            options: {
                pin: "left",
            },
        },
        {
            id: TransactionColumn.BANK_DESCRIPTION,
        },
        {
            id: TransactionColumn.COUNTERPARTY,
        },
        {
            id: TransactionColumn.CATEGORY,
        },
        {
            id: TransactionColumn.LEDGER,
        },
        {
            id: TransactionColumn.ACCOUNT,
        },
        {
            id: TransactionColumn.AMOUNT,
            options: {
                pin: "right",
            },
        },
        {
            id: TransactionColumn.ENTITY,
            options: {
                pin: "right",
            },
        },
    ],
    filters: {
        entityIds,
    },
});

export function useBuiltInTransactionViews(): Array<
    NamedView<TransactionView>
> {
    const entities = useEntities();
    const businessEntities = useEntities({ onlyBusiness: true });
    const standardCategories = useStandardCategories();
    const hasAnyClass = useClasses().classes.length > 0;
    const { enabled: isGlFirst } = useIsGlFirst();
    const isSchedulesEnabled = useIsRecognitionScheduleColumnEnabled();

    return useMemo(() => {
        if (entities.length === 0) {
            return [];
        }
        const entityIds = entities.map((entity) => entity.id);
        const businessEntityIds = businessEntities.map((entity) => entity.id);

        const transferTaxonomies = [
            Taxonomy.transfer,
            Taxonomy.credit_card_payment,
            Taxonomy.contributions_distributions,
        ];
        const transferCategoryIds = transferTaxonomies
            .map((taxonomy) => standardCategories[taxonomy]?.id)
            .filter((categoryId): categoryId is string => categoryId != null);

        return [
            createDefaultTransactionView({ entityIds, hasAnyClass, isGlFirst }),
            // GL-first workspaces classify by general ledger account, so the
            // dedicated General Ledger Review tab is dropped.
            ...(isGlFirst ? [] : [createLedgerReviewView(businessEntityIds)]),
            isGlFirst
                ? createGlFirstTransferReviewView({ entityIds })
                : createTransferReviewView({
                      entityIds,
                      transferCategoryIds,
                  }),
            createUnreconciledChecksView({
                entityIds,
                hasAnyClass,
                isGlFirst,
            }),
            // Gated on the same flag as the Schedule column config — without it
            // `buildColumns` would silently drop the column from this view.
            ...(isSchedulesEnabled
                ? [createSchedulesReviewView(businessEntityIds)]
                : []),
        ];
    }, [
        entities,
        businessEntities,
        standardCategories,
        hasAnyClass,
        isGlFirst,
        isSchedulesEnabled,
    ]);
}
